import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import type { ObjectLibraryItem } from './types';

/**
 * A producer's own shortcuts into the object library: the pieces they starred
 * and how often they have put each on a plan. Kept per person — an
 * administrator impersonating a planner, or two people sharing a machine,
 * each keep their own — in this browser, like the workspace layout.
 */
export interface LibraryPrefs {
  /** Library keys, in the order they were starred. */
  favourites: string[];
  /** Times each library key was put on a plan, and when it last was. */
  uses: Record<string, { count: number; lastUsed: string }>;
}

export const EMPTY_LIBRARY_PREFS: LibraryPrefs = { favourites: [], uses: {} };

/** How many pieces the Most used tab lists. */
export const MOST_USED_LIMIT = 8;

/**
 * The virtual tabs, shown before the library's own categories when they have
 * anything. Their ids cannot be a category name typed in Settings, so a real
 * category called "Favourites" keeps its own pieces; the labels are what shows.
 */
export const FAVOURITES_CATEGORY = '__favourites__';
export const MOST_USED_CATEGORY = '__most_used__';
const VIRTUAL_CATEGORY_LABELS = new Map<string, string>([
  [FAVOURITES_CATEGORY, 'Favourites'],
  [MOST_USED_CATEGORY, 'Most used'],
]);

// A Map, not an object: a category typed in Settings as `__proto__` or
// `toString` must stay a category, never be read as one of these.
export function isVirtualCategory(category: string): boolean {
  return VIRTUAL_CATEGORY_LABELS.has(category);
}

/** What a library tab says: a virtual tab's label, else the category itself. */
export function libraryCategoryLabel(category: string): string {
  return VIRTUAL_CATEGORY_LABELS.get(category) ?? category;
}

const storageKey = (userId: string) => `floorPlan:library:${userId}`;

/**
 * A library piece's identity. A piece from Settings is its row — two pieces
 * may share a name there, and a renamed one keeps its star. A built-in piece
 * is its type and name, since the type alone is not one: Round 60" and
 * Round 72" are both `TABLE`.
 */
export function libraryKey(
  item: Pick<ObjectLibraryItem, 'configId' | 'objectType' | 'name'>,
): string {
  return item.configId ? `config:${item.configId}` : `${item.objectType}|${item.name}`;
}

export function readLibraryPrefs(userId: string | undefined): LibraryPrefs {
  return readStoredPrefs(userId) ?? EMPTY_LIBRARY_PREFS;
}

/** What is stored, or `null` when storage cannot be read at all. */
function readStoredPrefs(userId: string | undefined): LibraryPrefs | null {
  if (!userId) return null;
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKey(userId));
  } catch {
    return null;
  }
  if (!raw) return EMPTY_LIBRARY_PREFS;
  try {
    const parsed = JSON.parse(raw) as Partial<LibraryPrefs>;
    const favourites = Array.isArray(parsed.favourites)
      ? parsed.favourites.filter((k): k is string => typeof k === 'string')
      : [];
    const uses: LibraryPrefs['uses'] = {};
    if (parsed.uses && typeof parsed.uses === 'object') {
      for (const [key, value] of Object.entries(parsed.uses)) {
        if (
          value &&
          Number.isInteger(value.count) &&
          value.count > 0 &&
          typeof value.lastUsed === 'string'
        ) {
          // A time that is not one would outrank every real use when times
          // are compared, so it is read as the earliest possible instead.
          const time = Date.parse(value.lastUsed);
          const lastUsed = new Date(Number.isNaN(time) ? 0 : time).toISOString();
          uses[key] = { count: value.count, lastUsed };
        }
      }
    }
    return { favourites: [...new Set(favourites)], uses };
  } catch {
    // Unreadable or not JSON: nothing stored worth keeping.
    return EMPTY_LIBRARY_PREFS;
  }
}

/** Whether the write landed; `false` when there is nobody or storage refused it. */
function writeLibraryPrefs(userId: string | undefined, prefs: LibraryPrefs): boolean {
  if (!userId) return false;
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(prefs));
    return true;
  } catch {
    // Storage unavailable (private mode, quota): the shortcuts are kept for the session only.
    return false;
  }
}

/** Stars a piece, or unstars one already starred. */
export function toggleFavourite(prefs: LibraryPrefs, key: string): LibraryPrefs {
  return setFavourite(prefs, key, !prefs.favourites.includes(key));
}

/** Stars or unstars a piece; asking for what is already so changes nothing. */
export function setFavourite(prefs: LibraryPrefs, key: string, on: boolean): LibraryPrefs {
  const has = prefs.favourites.includes(key);
  if (has === on) return prefs;
  const favourites = on ? [...prefs.favourites, key] : prefs.favourites.filter((k) => k !== key);
  return { ...prefs, favourites };
}

/** Counts one more use of a piece. */
export function recordUse(prefs: LibraryPrefs, key: string, now = new Date()): LibraryPrefs {
  const previous = prefs.uses[key];
  const at = now.toISOString();
  // A use replayed after storage refused it may be older than one another
  // tab has stored since: the later time stands (ISO strings sort by time).
  const lastUsed = previous && previous.lastUsed > at ? previous.lastUsed : at;
  const count = (previous?.count ?? 0) + 1;
  return { ...prefs, uses: { ...prefs.uses, [key]: { count, lastUsed } } };
}

/** The starred pieces still in the library, in the order they were starred. */
export function favouriteItems(
  library: ObjectLibraryItem[],
  prefs: LibraryPrefs,
): ObjectLibraryItem[] {
  const byKey = new Map<string, ObjectLibraryItem>();
  for (const item of library) if (!byKey.has(libraryKey(item))) byKey.set(libraryKey(item), item);
  return prefs.favourites
    .map((key) => byKey.get(key))
    .filter((item): item is ObjectLibraryItem => !!item);
}

/**
 * The pieces used most, most first; a tie goes to the one used last. Only
 * pieces still in the library — one removed in Settings drops out.
 */
export function mostUsedItems(
  library: ObjectLibraryItem[],
  prefs: LibraryPrefs,
  limit = MOST_USED_LIMIT,
): ObjectLibraryItem[] {
  return library
    .filter((item) => prefs.uses[libraryKey(item)])
    .sort((a, b) => {
      const ua = prefs.uses[libraryKey(a)];
      const ub = prefs.uses[libraryKey(b)];
      return ub.count - ua.count || ub.lastUsed.localeCompare(ua.lastUsed);
    })
    .slice(0, limit);
}

/** The signed-in person's library shortcuts, and the two ways they change. */
export function useLibraryPrefs() {
  const { user } = useAuth();
  const userId = user?.id;
  const [prefs, setPrefs] = useState<LibraryPrefs>(() => readLibraryPrefs(userId));
  const current = useRef(prefs);
  // This session's changes that storage has not taken yet (it refused the
  // write, or could not be read). They are replayed onto what is stored —
  // never the other way round — so neither this session's stars and counts
  // nor another tab's are lost, and the first write that lands clears them.
  const pending = useRef<Array<(prefs: LibraryPrefs) => LibraryPrefs>>([]);

  const show = useCallback((next: LibraryPrefs) => {
    current.current = next;
    setPrefs(next);
  }, []);

  /** What is stored with this session's pending changes on top, if storage can be read. */
  const storedWithPending = useCallback(() => {
    const stored = readStoredPrefs(userId);
    return stored && pending.current.reduce((p, change) => change(p), stored);
  }, [userId]);

  useEffect(() => {
    // Another person (impersonation switches user without a reload).
    pending.current = [];
    show(readLibraryPrefs(userId));
    // Another tab starring or placing a piece, so this one never shows a
    // star that is no longer the stored one.
    const onStorage = (e: StorageEvent) => {
      if (!userId || e.key !== storageKey(userId)) return;
      const next = storedWithPending();
      if (next) show(next);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [userId, show, storedWithPending]);

  /**
   * Applies a change to what is stored now, so a star or a count another tab
   * wrote a moment ago is kept rather than overwritten by this tab's older
   * copy. When storage cannot be read, it applies to what is on screen.
   */
  const update = useCallback(
    (change: (prefs: LibraryPrefs) => LibraryPrefs) => {
      pending.current = [...pending.current, change];
      const next = storedWithPending() ?? change(current.current);
      if (writeLibraryPrefs(userId, next)) pending.current = [];
      show(next);
    },
    [userId, show, storedWithPending],
  );

  // The star's new state is decided by what the person can see, so applying
  // it to a newer stored copy never reverses what they pressed.
  const toggle = useCallback(
    (item: ObjectLibraryItem) => {
      const key = libraryKey(item);
      const on = !current.current.favourites.includes(key);
      update((p) => setFavourite(p, key, on));
    },
    [update],
  );
  // The time is taken when the piece is placed, not when the change is
  // applied: a change replayed after storage refused it keeps its own time.
  const used = useCallback(
    (item: ObjectLibraryItem) => {
      const key = libraryKey(item);
      const at = new Date();
      update((p) => recordUse(p, key, at));
    },
    [update],
  );

  return { prefs, toggleFavourite: toggle, recordUse: used };
}
